import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Search, Loader2, X, ArrowDownToLine, Clock, Tag, Globe2, Activity } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LABEL = {
  join: "Join the Temple",
  newsletter: "Newsletter subscriber",
  membership_waitlist: "Membership waitlist",
  level_up_application: "LEVEL UP application",
  partnership: "Partnership enquiry",
  home_education: "Home Education enquiry",
  ubuntu_retreat: "Ubuntu Retreat booking",
  corporate_enquiry: "Corporate enquiry",
  ticket_purchase: "Ticket purchase",
};

const TYPE_TONE = {
  join: "bg-amber-400/15 text-amber-300 border-amber-400/40",
  newsletter: "bg-sky-400/15 text-sky-300 border-sky-400/40",
  membership_waitlist: "bg-fuchsia-400/15 text-fuchsia-300 border-fuchsia-400/40",
  level_up_application: "bg-emerald-400/15 text-emerald-300 border-emerald-400/40",
  partnership: "bg-purple-400/15 text-purple-300 border-purple-400/40",
  home_education: "bg-blue-400/15 text-blue-300 border-blue-400/40",
  ubuntu_retreat: "bg-rose-400/15 text-rose-300 border-rose-400/40",
  corporate_enquiry: "bg-orange-400/15 text-orange-300 border-orange-400/40",
  ticket_purchase: "bg-lime-400/15 text-lime-300 border-lime-400/40",
};

const SCORE_TONE = {
  none: "bg-zinc-800 text-zinc-400",
  new: "bg-zinc-700 text-zinc-200",
  active: "bg-amber-400/30 text-amber-200 border border-amber-400/50",
  deep: "bg-amber-400 text-black",
};

function fmtDate(v) {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return String(v);
  }
}

import { authHeaders } from "@/lib/adminAuth";

export default function CustomerJourney() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const search = async (e) => {
    e?.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Enter a valid email to search.");
      return;
    }
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await axios.get(`${API}/admin/customer-journey`, {
        params: { email },
        headers: authHeaders(),
      });
      setResult(res.data);
      if (res.data.total_touchpoints === 0) {
        toast.info("No touchpoints for that email yet.");
      } else {
        toast.success(`${res.data.total_touchpoints} touchpoint(s) found.`);
      }
    } catch (err) {
      setError(err?.response?.data?.detail || "Search failed.");
      toast.error(err?.response?.data?.detail || "Search failed.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setResult(null);
    setEmail("");
    setError(null);
  };

  const exportJourney = () => {
    if (!result) return;
    const csv = [
      ["timestamp", "type", "summary", "referral_source", "page_source"],
      ...result.timeline.map((t) => [
        t.created_at, t.type, t.summary || "",
        t.referral_source || "", t.page_source || "",
      ]),
    ].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `tok-journey-${result.email.replace(/[^a-z0-9]+/gi, "-")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <section
      data-testid="customer-journey"
      className="mb-10 border border-amber-400/30 bg-gradient-to-br from-amber-400/5 via-black to-black p-6 sm:p-8"
    >
      <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
        <div>
          <p className="tok-eyebrow flex items-center gap-2">
            <Activity className="h-3.5 w-3.5" />
            Customer Journey
          </p>
          <h3 className="tok-headline-md mt-2">
            Search any email · see every touchpoint
          </h3>
        </div>
        {result && (
          <div className="flex items-center gap-2">
            <button
              onClick={exportJourney}
              data-testid="journey-export"
              className="inline-flex items-center gap-2 border border-amber-400/40 hover:border-amber-400 hover:text-amber-400 text-zinc-300 px-3 py-2 text-[11px] uppercase tracking-[0.2em] font-bold transition-colors"
            >
              <ArrowDownToLine className="h-3.5 w-3.5" />
              Export
            </button>
            <button
              onClick={reset}
              data-testid="journey-reset"
              className="inline-flex items-center gap-2 border border-white/10 hover:border-amber-400 text-zinc-300 px-3 py-2 text-[11px] uppercase tracking-[0.2em] font-bold transition-colors"
            >
              <X className="h-3.5 w-3.5" />
              Clear
            </button>
          </div>
        )}
      </div>

      <form onSubmit={search} className="flex gap-2">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="e.g. amma@example.com"
          data-testid="journey-email-input"
          className="flex-1 bg-black border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-amber-400"
        />
        <button
          type="submit"
          disabled={loading}
          data-testid="journey-search-button"
          className="inline-flex items-center gap-2 bg-amber-400 hover:bg-amber-300 disabled:opacity-60 text-black px-5 py-3 text-[11px] uppercase tracking-[0.22em] font-bold transition-colors"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          Search
        </button>
      </form>

      {error && (
        <p className="mt-4 text-sm text-rose-400" data-testid="journey-error">
          {error}
        </p>
      )}

      {result && (
        <div className="mt-7 space-y-6" data-testid="journey-result">
          {/* Summary tiles */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Tile label="Touchpoints" value={result.total_touchpoints} />
            <Tile
              label="Engagement"
              value={result.engagement_score.toUpperCase()}
              toneClass={SCORE_TONE[result.engagement_score]}
              testId="journey-engagement"
            />
            <Tile
              label="First seen"
              value={fmtDate(result.first_seen)}
              small
            />
            <Tile
              label="Last seen"
              value={fmtDate(result.last_seen)}
              small
            />
          </div>

          {/* Surface counts */}
          {Object.keys(result.by_source || {}).length > 0 && (
            <div>
              <p className="tok-eyebrow-sm mb-3">Surfaces touched</p>
              <div className="flex flex-wrap gap-2" data-testid="journey-surfaces">
                {Object.entries(result.by_source).map(([k, v]) => (
                  <span
                    key={k}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] font-bold border ${TYPE_TONE[k] || "border-white/10 text-zinc-300"}`}
                  >
                    {TYPE_LABEL[k] || k}
                    <span className="px-1.5 py-0.5 bg-black/40 text-[10px]">{v}</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Interests + referrals + pages */}
          <div className="grid md:grid-cols-3 gap-5">
            <FactColumn
              icon={Tag}
              label="All interests"
              items={result.all_interests}
            />
            <FactColumn
              icon={Globe2}
              label="Referral sources"
              items={result.all_referral_sources}
            />
            <FactColumn
              icon={Clock}
              label="Pages visited"
              items={result.all_pages}
            />
          </div>

          {/* Timeline */}
          <div>
            <p className="tok-eyebrow-sm mb-3">Timeline (newest first)</p>
            <ol
              className="space-y-2 max-h-[480px] overflow-y-auto pr-2"
              data-testid="journey-timeline"
            >
              {result.timeline.map((t) => (
                <li
                  key={`${t.type}-${t.id || t.created_at}`}
                  data-testid={`journey-event-${t.type}`}
                  className="border border-white/5 bg-zinc-950 p-4"
                >
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <span
                      className={`px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] font-bold border ${TYPE_TONE[t.type] || "border-white/10 text-zinc-300"}`}
                    >
                      {TYPE_LABEL[t.type] || t.type}
                    </span>
                    <span className="text-xs text-zinc-500">
                      {fmtDate(t.created_at)}
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-zinc-200 break-all">
                    {t.summary || "—"}
                  </p>
                  {(t.referral_source || t.page_source) && (
                    <p className="mt-1 text-[11px] text-zinc-500 flex flex-wrap gap-3">
                      {t.referral_source && <span>via {t.referral_source}</span>}
                      {t.page_source && <span>on {t.page_source}</span>}
                    </p>
                  )}
                </li>
              ))}
            </ol>
          </div>
        </div>
      )}
    </section>
  );
}

function Tile({ label, value, toneClass = "bg-zinc-950 border border-white/5", small = false, testId }) {
  return (
    <div className={`p-4 ${toneClass}`} data-testid={testId}>
      <p className="text-[9px] uppercase tracking-[0.28em] font-bold text-zinc-500">
        {label}
      </p>
      <p className={`mt-1 font-display font-black ${small ? "text-sm" : "text-2xl"}`}>
        {value}
      </p>
    </div>
  );
}

function FactColumn({ icon: Icon, label, items = [] }) {
  return (
    <div className="border border-white/5 bg-zinc-950 p-4">
      <p className="tok-eyebrow-sm flex items-center gap-2">
        <Icon className="h-3 w-3" />
        {label}
      </p>
      <ul className="mt-3 space-y-1.5 text-sm text-zinc-300">
        {items.length ? (
          items.map((i) => (
            <li key={i} className="break-all">
              · {i}
            </li>
          ))
        ) : (
          <li className="text-zinc-600">—</li>
        )}
      </ul>
    </div>
  );
}
